// _shared/hooks/usePackages.js
import { useState, useEffect, useRef } from 'react';
import { packagesService } from '../../_services/packagesService';

export const usePackages = (slug, isEditable = false) => {
  // Estados principales
  const [packages, setPackages] = useState([]);
  const [packageForms, setPackageForms] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  
  const hasFetched = useRef(false);
  const fetchedSlug = useRef(null);

  useEffect(() => {
    const fetchPackages = async () => {
      if (!slug) return;
      // 🟢 FIX: si cambia el slug (otro evento), se debe volver a hacer fetch.
      // Antes hasFetched quedaba en true para siempre y el hook nunca
      // recargaba los paquetes del nuevo evento.
      if (hasFetched.current && fetchedSlug.current === slug) return;

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const loadedPackages = await packagesService.getPackages(slug, token);
        
        setPackages(loadedPackages);
        setPackageForms(JSON.parse(JSON.stringify(loadedPackages)));
        hasFetched.current = true;
        fetchedSlug.current = slug;
      } catch (error) {
        console.error('Error al cargar paquetes:', error);
        setError('Error al cargar los paquetes');
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [slug]);

  /**
   * Seleccionar paquete
   */
  const handleSelectPackage = (pkg, onSelectPackage, onTabChange) => {
    const pkgConMonto = packagesService.createPackageForSelection(pkg, selectedAddons);

    if (onSelectPackage) {
      onSelectPackage(pkgConMonto);
    }

    if (onTabChange) {
      onTabChange('Formulario', { selectedPackage: pkgConMonto });
    }
  };

  /**
   * Toggle addon
   */
  const handleAddonToggle = (pkgId, addonId) => {
    setSelectedAddons(prev => ({
      ...prev,
      [pkgId]: {
        ...prev[pkgId],
        [addonId]: !prev[pkgId]?.[addonId]
      }
    }));
  };

  /**
   * Gestionar addons en admin
   */
  const handleAddAddon = (packageIndex) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[packageIndex] };
      pkg.addons = [
        ...(pkg.addons || []),
        { id: `addon-${Date.now()}`, label: '', extra_amount: 0 },
      ];
      updated[packageIndex] = pkg;
      return updated;
    });
  };

  const handleAddonChange = (packageIndex, addonIndex, field, value) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[packageIndex] };
      const addons = [...(pkg.addons || [])];
      addons[addonIndex] = { ...addons[addonIndex], [field]: value };
      pkg.addons = addons;
      updated[packageIndex] = pkg;
      return updated;
    });
  };

  const handleDeleteAddon = (packageIndex, addonIndex) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[packageIndex] };
      pkg.addons = (pkg.addons || []).filter((_, i) => i !== addonIndex);
      updated[packageIndex] = pkg;
      return updated;
    });
  };

  /**
   * Manejar imagen
   */
  const handleImageChange = (index, file) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[index] };
      // 🟢 FIX: liberar el blob URL anterior para no acumular memory leaks
      if (pkg.imagePreview) {
        URL.revokeObjectURL(pkg.imagePreview);
      }
      pkg.imageFile = file;
      pkg.imagePreview = URL.createObjectURL(file);
      updated[index] = pkg;
      return updated;
    });
  };

  /**
   * Guardar paquetes
   */
  const handleSavePackages = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Validar todos los paquetes
      for (const [index, pkg] of packageForms.entries()) {
        const error = packagesService.validatePackage(pkg);
        if (error) {
          alert(`Error en paquete "${pkg.title || 'sin título'}": ${error}`);
          setIsSaving(false);
          return;
        }
      }

      const token = localStorage.getItem('token');
      const response = await packagesService.savePackages(slug, packageForms, token);

      if (response.message) {
        alert(response.message);
      } else {
        alert('Paquetes guardados correctamente.');
      }

      // Actualizar estado
      if (response.packages) {
        const updatedPackages = response.packages.map(pkg => ({
          ...pkg,
          id_package: pkg.id_package || pkg.id || null,
          startDate: pkg.startDate ? pkg.startDate.split('T')[0] : '',
          endDate: pkg.endDate ? pkg.endDate.split('T')[0] : '',
          addons: pkg.addons || [],
          image: pkg.image || null,
          imageFile: null,
          imagePreview: null,
        }));
        setPackages(updatedPackages);
        setPackageForms(JSON.parse(JSON.stringify(updatedPackages)));
      }

      return true;
    } catch (error) {
      console.error('Error al guardar paquetes:', error);
      
      let errorMessage = 'Error al guardar los paquetes';
      
      if (error.response) {
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 404) {
          errorMessage = 'La ruta no existe. Verifica que el slug sea correcto.';
        } else if (error.response.status === 401) {
          errorMessage = 'No tienes autorización. Inicia sesión nuevamente.';
        } else if (error.response.status === 400) {
          errorMessage = 'Datos inválidos. Verifica la información.';
        } else if (error.response.status === 413) {
          errorMessage = 'Los archivos son demasiado grandes.';
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tiempo de espera agotado. Intenta nuevamente.';
      }
      
      alert(errorMessage);
      setError(errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Manejar cambios en formulario
   */
  const handleFormChange = (index, field, value) => {
    setPackageForms(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  /**
   * Manejar includes
   */
  const handleIncludeChange = (pkgIndex, incIndex, field, value) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[pkgIndex] };
      const includes = [...(pkg.includes || [])];
      includes[incIndex] = { ...includes[incIndex], [field]: value };
      pkg.includes = includes;
      updated[pkgIndex] = pkg;
      return updated;
    });
  };

  const handleAddInclude = (pkgIndex) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[pkgIndex] };
      pkg.includes = [
        ...(pkg.includes || []),
        { id: `inc-${Date.now()}`, label: '', required: false },
      ];
      updated[pkgIndex] = pkg;
      return updated;
    });
  };

  const handleDeleteInclude = (pkgIndex, incIndex) => {
    setPackageForms(prev => {
      const updated = [...prev];
      const pkg = { ...updated[pkgIndex] };
      pkg.includes = (pkg.includes || []).filter((_, i) => i !== incIndex);
      updated[pkgIndex] = pkg;
      return updated;
    });
  };

  /**
   * Crear nuevo paquete
   */
  const handleAddPackageForm = () => {
    setPackageForms(prev => [...prev, packagesService.createEmptyPackage()]);
  };

  /**
   * Eliminar paquete
   */
  const handleDeletePackageForm = (index) => {
    if (window.confirm('¿Seguro que deseas eliminar este paquete?')) {
      setPackageForms(prev => prev.filter((_, i) => i !== index));
    }
  };

  /**
   * Calcular monto total (para vista pública)
   */
  const calculateTotalAmount = (pkg) => {
    return packagesService.calculateTotalAmount(pkg, selectedAddons);
  };

  /**
   * Convertir a USD (para vista pública)
   */
  const convertToUSD = (pkg, totalBs) => {
    return packagesService.convertToUSD(pkg, totalBs);
  };

  /**
   * Formatear fecha
   */
  const formatDate = (dateString) => {
    return packagesService.formatDate(dateString);
  };

  /**
   * Obtener etiqueta de competidor
   */
  const getCompetitorLabel = (numberOfCompetitors) => {
    return packagesService.getCompetitorLabel(numberOfCompetitors);
  };

  return {
    // Estado
    packages,
    packageForms,
    selectedAddons,
    isSaving,
    loading,
    error,

    // Setters
    setPackages,
    setPackageForms,
    setSelectedAddons,

    // Handlers
    handleSelectPackage,
    handleAddonToggle,
    handleAddAddon,
    handleAddonChange,
    handleDeleteAddon,
    handleImageChange,
    handleSavePackages,
    handleFormChange,
    handleIncludeChange,
    handleAddInclude,
    handleDeleteInclude,
    handleAddPackageForm,
    handleDeletePackageForm,

    // Utilidades
    calculateTotalAmount,
    convertToUSD,
    formatDate,
    getCompetitorLabel,
  };
};